import { MenuItem } from "../../components/navigation/MainMenu";
import { INavigationMenuItem } from "../../types/NavigationMenu";
import { getNavigationIcon } from "./iconMapping";
import {
  isExternalUrl,
  openExternalLink
} from "../nativeUtils/openExternalLink";

export interface TransformedNavigationItem extends MenuItem {
  originalData: INavigationMenuItem;
  hasChildren: boolean;
  venuePicker?: any;
}

export interface TransformNavigationOptions {
  forceActiveItemId?: string;
}

/**
 * Navigates to the given link, opening external URLs in the device
 * browser and internal URLs via location assignment.
 */
export const navigateToLink = (
  item: INavigationMenuItem,
  link?: string
): void => {
  item.function?.();
  if (link) {
    if (isExternalUrl(link)) {
      openExternalLink(link);
    } else {
      window.location.href = link;
    }
  }
};

/**
 * Maps ToggleView identifiers to their corresponding path segments.
 */
export const getViewPath = (view?: string): string => {
  switch (view) {
    case "DiaryTab":
      return "diary";
    case "FloorplanTab":
      return "floorplan";
    case "RunsheetTab":
      return "run-sheet";
    default:
      return "";
  }
};

/**
 * Transforms child navigation items into the shape expected by the
 * menu component, de-duplicating the mapping logic that was previously
 * repeated for both the forced-active and standard code paths.
 */
const transformChildren = (
  children: INavigationMenuItem[] | undefined,
  currentPath?: string
) =>
  children?.map((child) => ({
    id: child.text.toLowerCase().replace(/\s+/g, "-"),
    label: child.text,
    icon: child.icon ? getNavigationIcon(child.icon) : undefined,
    isActive: currentPath !== undefined ? currentPath === child.link : false,
    onClick: () => navigateToLink(child, child.link),
    badge: typeof child.badgeCount === "number" ? child.badgeCount : undefined,
    originalData: child
  }));

export const transformNavigationData = (
  navigationData: INavigationMenuItem[],
  currentPath: string = "/",
  currentVenueId?: string,
  currentView?: string,
  options?: TransformNavigationOptions
): TransformedNavigationItem[] => {
  return navigationData.map((item) => {
    const itemId = item.text.toLowerCase().replace(/\s+/g, "-");

    // If forceActiveItemId is set, use it to determine active state
    if (options?.forceActiveItemId) {
      const isActive = itemId === options.forceActiveItemId;

      return {
        id: itemId,
        label: item.text,
        icon: getNavigationIcon(item.icon ?? "Dashboard"),
        isActive,
        onClick: () => navigateToLink(item, item.link),
        children: transformChildren(item.children),
        hasChildren: (item.children?.length ?? 0) > 0,
        hasVenuePicker: !!item.venue_picker?.path,
        venuePickerPath: item.venue_picker?.path,
        venuePicker: item.venue_picker,
        originalData: item
      };
    }

    // Check if this item should be active based on current venue and view
    const viewPath = getViewPath(currentView);
    const isVenuePickerActive =
      item.venue_picker?.path &&
      currentVenueId &&
      currentView &&
      viewPath &&
      item.venue_picker.path.includes(viewPath) &&
      currentPath.includes(currentVenueId);

    const isActive = Boolean(
      currentPath === item.link ||
      currentPath === item.venue_picker?.path ||
      isVenuePickerActive
    );

    return {
      id: item.text.toLowerCase().replace(/\s+/g, "-"),
      label: item.text,
      icon: getNavigationIcon(item.icon ?? "Dashboard"),
      isActive,
      onClick: () => navigateToLink(item, item.link),
      children: transformChildren(item.children, currentPath),
      hasChildren: (item.children?.length ?? 0) > 0,
      hasVenuePicker: !!item.venue_picker?.path,
      venuePickerPath: item.venue_picker?.path,
      venuePicker: item.venue_picker,
      originalData: item
    };
  });
};

export const separateTopAndBottomItems = (
  navigationData: INavigationMenuItem[]
): { topItems: INavigationMenuItem[]; bottomItems: INavigationMenuItem[] } => {
  const topItems = navigationData.filter((item) => !item.bottom);
  const bottomItems = navigationData.filter((item) => item.bottom);

  return { topItems, bottomItems };
};
